import { useMemo } from "react";
import Button from "ui/buttons/Button";
import type { ModalProps } from "ui/components/Modal";
import Modal from "ui/components/Modal";
import type { TreeSelectNodeType } from "ui/components/TreeSelect";
import { TreeSelect } from "ui/components/TreeSelect";
import styles from "./PlanLibraryLocationModal.module.scss";

interface PlanLibraryLocationModalProps {
  modalProps: ModalProps;
  treeData: TreeSelectNodeType[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onLeafSelectionChange?: (ids: string[]) => void;
  defaultExpandedIds?: string[];
  existingCount?: number;
  onBack?: () => void;
  onReview: () => void;
}

export default function PlanLibraryLocationModal({
  modalProps,
  treeData,
  selectedIds,
  onSelectionChange,
  onLeafSelectionChange,
  defaultExpandedIds,
  existingCount,
  onBack,
  onReview,
}: PlanLibraryLocationModalProps) {
  // Count how many existing locations are in the current selection
  const existingInSelection = useMemo(
    () =>
      selectedIds.filter((id) => {
        const findNode = (nodes: TreeSelectNodeType[]): TreeSelectNodeType | undefined => {
          for (const node of nodes) {
            if (node.id === id) {
              return node;
            }
            const found = node.children ? findNode(node.children) : undefined;
            if (found) {
              return found;
            }
          }
          return undefined;
        };
        const node = findNode(treeData);
        return node?.disabled === true;
      }).length,
    [selectedIds, treeData],
  );

  const hasNewSelections = selectedIds.length > existingInSelection;

  return (
    <Modal
      {...modalProps}
      size={modalProps.size ?? "small"}
      title="Add to Locations"
      variants={["bodyPadding", "footerButtons"]}
      footer={
        <>
          <Button btnSize="large" btnStyle="tertiary" onClick={onBack ?? modalProps.onClose}>
            Back
          </Button>
          <Button btnSize="large" onClick={onReview} disabled={!hasNewSelections}>
            Review
          </Button>
        </>
      }
    >
      <div>
        <h4 className={styles.header}>Select locations to add this plan to.</h4>
        {typeof existingCount === "number" && (
          <div className={styles.existingCount}>{existingCount} existing locations</div>
        )}

        <TreeSelect
          treeData={treeData}
          dataLabel="locations"
          onSelectionChange={onSelectionChange}
          onLeafSelectionChange={onLeafSelectionChange}
          defaultExpandedIds={defaultExpandedIds ?? selectedIds}
        />
      </div>
    </Modal>
  );
}
